<template>
  <div class="container-fluid h-100">
    <div class="row mt-3 justify-content-center">
      <div class="col-md-6 col-sm-10">
        <div class="card">
          <div class="row">
            <div class="col">
              <h3 class="font-weight-light ml-3 mr-auto pull-left">Redefinir Senha</h3>
            </div>
          </div>
          <hr />

          <div class="row">
            <div class="col-7">
              <div class="row justify-content-center">
                <div class="col">
                  <div class="form-group mb-4">
                    <label>Digite a sua nova senha:</label>
                    <PasswordStrength v-model="password" :maxlength="20" :disabled="expiredLink" />
                    <small v-if="password.length && v$.password.$invalid" class="text-danger">
                      A senha deve ter 8-20 caracteres, 1 maiúscula, 1 minúscula, 1 número e 1
                      caractere especial (Ex: Ex4plo@123).
                    </small>
                  </div>
                </div>
              </div>

              <div class="row justify-content-center">
                <div class="col">
                  <div class="form-group mb-4">
                    <label>Repita a sua nova senha:</label>
                    <PasswordStrength
                      v-model="confirmPassword"
                      :maxlength="20"
                      :disabled="expiredLink"
                    />
                    <small
                      v-if="confirmPassword.length && v$.confirmPassword.$invalid"
                      class="text-danger"
                    >
                      As senhas não coincidem
                    </small>
                  </div>
                </div>
              </div>
            </div>
            <div class="col">
              <h5><strong>Como sua senha deve ser</strong></h5>
              <h6>Não coloque seu nome, e-mail nem caracteres repetidos.</h6>
              <h6>
                A senha precisa ter 8-20 caracteres, conter 1 maiúscula, 1 minúscula, 1 número e 1
                caractere especial.
              </h6>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row justify-content-center">
      <div class="col-lg-2 col-sm-4">
        <button
          class="btn btn-primary p-3 h4 w-100"
          :disabled="v$.$invalid || loading"
          @click.prevent="confirmNewPassword"
        >
          <span v-if="loading">Aguarde...</span>
          <span v-else>Confirmar</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import useVuelidate from '@vuelidate/core';
import { required, minLength, sameAs } from '@vuelidate/validators';
import { password as pwdRule } from '@/validations/password';

import Swal from 'sweetalert2';
import AccountApi from '@/services/AccountApi';
import PasswordStrength from './PasswordStrength.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();

const password = ref('');
const confirmPassword = ref('');
const loading = ref(false);
const expiredLink = ref(false);

const rules = {
  password: { required, minLength: minLength(8), password: pwdRule },
  confirmPassword: { required, sameAsPassword: sameAs(password) },
};
const v$ = useVuelidate(rules, { password, confirmPassword });

const ACCOUNT_API = new AccountApi();

onMounted(async () => {
  try {
    const { token } = route.query;

    const signInResult = await ACCOUNT_API.autoSignIn(token);
    if (signInResult.status !== 200) {
      expiredLink.value = true;
      await showExpiredLinkModal();
      return;
    }
    store.dispatch('account/login', signInResult.data);
  } catch (_error) {
    expiredLink.value = true;
    await showExpiredLinkModal();
  }
});

async function confirmNewPassword() {
  if (v$.value.$invalid) {
    return;
  }
  loading.value = true;

  const result = await ACCOUNT_API.createNewPassword(password.value);

  loading.value = false;
  if (result.status !== 200) {
    return Swal.fire({
      icon: 'error',
      title: 'Ops...',
      text: 'Ocorreu um erro ao tentar redefinir a senha!',
    });
  }

  await store.dispatch('account/logout');
  await Swal.fire({
    icon: 'success',
    title: 'Senha cadastrada com sucesso!',
  });
  router.push('/acessar');
}

async function showExpiredLinkModal() {
  const { value } = await Swal.fire({
    icon: 'error',
    title: 'Ops...',
    text: 'O link que você está tentando acessar expirou!',
    showConfirmButton: true,
    confirmButtonText: 'Enviar novo link',
    allowOutsideClick: false,
  });
  if (value) {
    displayNewLinkModal();
  }
}

function displayNewLinkModal() {
  Swal.fire({
    title: 'Informe o endereço de e-mail',
    input: 'email',
    showLoaderOnConfirm: true,
    confirmButtonText: 'Enviar',
    preConfirm: async email => {
      const res = await ACCOUNT_API.sendPasswordResetEmail(email);
      if (res.status !== 204) {
        Swal.showValidationMessage('Erro ao enviar o link');
      }
    },
  }).then(({ value }) => {
    if (value) {
      Swal.fire({
        icon: 'success',
        text: 'Link enviado com sucesso',
        showConfirmButton: false,
      });
    }
  });
}
</script>
